import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  AlertTriangle, Loader2, Trash2, RefreshCw, ShieldCheck, Calendar, Clock,
  ChevronDown, ChevronUp, CheckCircle2, Sparkles,
} from 'lucide-react';

const SERVICE_LABELS = {
  day_program: 'Explore',
  employment: 'Employment',
  in_home: 'IHCS',
};
const SERVICE_BADGE = {
  day_program: 'bg-[#EDE7F6] text-[#5A2F8E]',
  employment: 'bg-[#FFF8E1] text-[#9A6D00]',
  in_home: 'bg-[#E0F2F1] text-[#00695C]',
};

export default function DuplicateShiftsCleanup({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/duplicate-shifts');
      setData(data);
    } catch {
      toast.error('Failed to scan for duplicates');
      setData({ summary: { groups_count: 0, total_dupes: 0, total_rows: 0 }, groups: [] });
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cleanupAll = async () => {
    if (!data || data.summary.total_dupes === 0) return;
    const ok = window.confirm(
      `Delete ${data.summary.total_dupes} duplicate shift row${data.summary.total_dupes === 1 ? '' : 's'}?\n\n` +
      `For each duplicate group (${data.summary.groups_count}), the OLDEST shift will be kept and the rest deleted.\n\n` +
      `This cannot be undone.`
    );
    if (!ok) return;
    setRunning(true);
    try {
      const { data: res } = await api.post('/admin/duplicate-shifts/cleanup', { mode: 'keep_oldest' });
      toast.success(`Cleaned up ${res.deleted} duplicate shift${res.deleted === 1 ? '' : 's'}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    }
    setRunning(false);
  };

  const deleteIds = async (ids, label) => {
    if (!ids?.length) return;
    if (!window.confirm(`Delete ${ids.length} shift${ids.length === 1 ? '' : 's'} for ${label}?`)) return;
    try {
      const { data: res } = await api.post('/admin/duplicate-shifts/cleanup', { mode: 'delete_ids', shift_ids: ids });
      toast.success(`Deleted ${res.deleted} shift${res.deleted === 1 ? '' : 's'}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const toggleExpand = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  if (loading) {
    return <Card className="border-[#DED8EB]"><CardContent className="p-12 text-center"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0] mx-auto" /></CardContent></Card>;
  }

  const { summary, groups } = data;
  const isClean = summary.total_dupes === 0;

  return (
    <div className="space-y-4" data-testid="duplicate-shifts-cleanup">
      {/* Header / status */}
      <Card className={`border-2 ${isClean ? 'border-[#A5D6A7] bg-[#E8F5E9]' : 'border-[#FFB74D] bg-[#FFF3E0]'}`}>
        <CardContent className="p-5 flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            {isClean ? (
              <ShieldCheck className="h-9 w-9 text-[#1B5E20] shrink-0" />
            ) : (
              <AlertTriangle className="h-9 w-9 text-[#E65100] shrink-0" />
            )}
            <div>
              <h3 className="text-lg font-bold" style={{ color: isClean ? '#1B5E20' : '#5D2E00' }}>
                {isClean ? 'Schedule data is clean' : 'Duplicate shifts detected'}
              </h3>
              <p className="text-sm mt-0.5" style={{ color: isClean ? '#2E7D32' : '#E65100' }}>
                {isClean
                  ? 'No staff member is scheduled twice for the same time block. Schedule integrity is solid.'
                  : `Found ${summary.total_dupes} duplicate row${summary.total_dupes === 1 ? '' : 's'} across ${summary.groups_count} affected staff/time block${summary.groups_count === 1 ? '' : 's'}.`}
              </p>
              {!isClean && (
                <p className="text-xs mt-1 text-[#8D5A00]">
                  These exist from before the double-booking detector was added. Each "group" is one staff member assigned to multiple shift rows at exactly the same time on the same day.
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={fetchData}
              variant="outline"
              size="sm"
              className="border-[#DED8EB] text-[#5A4D6E] gap-1.5"
              data-testid="cleanup-rescan-btn"
            >
              <RefreshCw className="h-3.5 w-3.5" />Rescan
            </Button>
            {!isClean && (
              <Button
                onClick={cleanupAll}
                disabled={running}
                className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white gap-1.5 shadow-sm"
                data-testid="cleanup-all-btn"
              >
                {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {running ? 'Cleaning...' : `Auto-clean ${summary.total_dupes} duplicates`}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Per-group breakdown */}
      {!isClean && (
        <Card className="border-[#DED8EB]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#2D1B4E]">Review each duplicate group</CardTitle>
            <p className="text-xs text-[#8E85A0]">Auto-clean keeps the oldest record. Expand a group to manually choose which to delete.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {groups.map(g => {
              const isOpen = !!expanded[g.key];
              return (
                <div key={g.key} className="border border-[#DED8EB] rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleExpand(g.key)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#FAF8FE] transition-colors text-left"
                    data-testid={`dupe-row-${g.key}`}
                  >
                    {isOpen ? <ChevronUp className="h-4 w-4 text-[#8E85A0] shrink-0" /> : <ChevronDown className="h-4 w-4 text-[#8E85A0] shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-[#2D1B4E]">{g.employee_name || '(Unknown staff)'}</span>
                        <Badge className="text-[10px] bg-[#FFEBEE] text-[#C62828] border border-[#EF9A9A]">
                          {g.count} rows · {g.duplicates} duplicate{g.duplicates === 1 ? '' : 's'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-[11px] text-[#8E85A0]">
                        <span className="flex items-center gap-1"><Calendar className="h-2.5 w-2.5" />{formatDate(g.date)}</span>
                        <span className="flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{g.time_in}–{g.time_out}</span>
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-[#F0EDF6] bg-[#FAF8FE] p-3 space-y-2">
                      {g.shifts.map((s, i) => (
                        <div key={s.id} className="flex items-center gap-3 bg-white border border-[#DED8EB] rounded-md px-3 py-2" data-testid={`dupe-shift-${s.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {i === 0 && (
                                <Badge className="text-[9px] bg-[#E8F5E9] text-[#1B5E20] border border-[#81C784]">
                                  <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />OLDEST · keep
                                </Badge>
                              )}
                              <Badge className={`text-[9px] ${SERVICE_BADGE[s.service_line] || 'bg-[#F0EDF6] text-[#5A4D6E]'}`}>
                                {SERVICE_LABELS[s.service_line] || s.service_line}
                              </Badge>
                              {s.group_number && (
                                <span className="text-[10px] text-[#8E85A0]">Group {s.group_number}</span>
                              )}
                              {s.location && <span className="text-[10px] text-[#8E85A0]">· {s.location}</span>}
                              {(s.participants || []).length > 0 && (
                                <span className="text-[10px] text-[#8E85A0]">· {s.participants.length} participant{s.participants.length === 1 ? '' : 's'}</span>
                              )}
                            </div>
                            <p className="text-[10px] text-[#8E85A0] mt-0.5 font-mono">id: {s.id?.slice(0, 8)}… · created {(s.created_at || '').slice(0, 10)}</p>
                          </div>
                          {i > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteIds([s.id], `${g.employee_name} · ${g.date}`)}
                              className="h-7 text-[10px] text-[#D9534F] border-red-300 hover:bg-red-50"
                              data-testid={`dupe-delete-${s.id}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />Delete
                            </Button>
                          )}
                        </div>
                      ))}
                      {g.duplicates > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteIds(g.shifts.slice(1).map(s => s.id), `${g.employee_name} · ${g.date}`)}
                          className="w-full h-8 text-xs text-[#D32F2F] border-[#FFB74D] hover:bg-[#FFF3E0]"
                          data-testid={`dupe-clean-group-${g.key}`}
                        >
                          <Sparkles className="h-3 w-3 mr-1.5" />
                          Clean this group (delete {g.duplicates} duplicate{g.duplicates === 1 ? '' : 's'})
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}
